import React, { FormEvent, useEffect, useState } from 'react';
import './ProfilePage.css';
import { useAuth } from '../../common/Auth/AuthProvider';
import { useCart } from '../../../contexts/CartContext';
import EmailIcon from '@mui/icons-material/Email';
import GoogleIcon from '@mui/icons-material/Google';
import PhoneIphoneIcon from '@mui/icons-material/PhoneIphone';
import TelegramIcon from '@mui/icons-material/Telegram';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import Button from '@mui/material/Button';
import {
  Alert,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  MenuItem,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { ApiError } from '../../common/Rest/ReaderlyHttpClient';
import UsersService from '../../common/Rest/Services/UserService/UsersService';
import {
  AuthConnectionType,
  DeliveryDestination,
  DeliveryInformationData,
  DeliveryInformationPayload,
  DeliveryProvider,
  UserInfoData,
} from '../../common/Rest/Services/UserService/Data';
import orderService, { OrderData } from '../../common/Rest/Services/OrderService/OrderService';
import DeliveryProviderIcon from '../../common/Delivery/DeliveryProviderIcon';
import NovaPoshtaAddressFields from '../../common/Delivery/NovaPoshtaAddressFields';
import SelfPickupFields from '../../common/Delivery/SelfPickupFields';
import { requestTelegramLoginData } from '../LoginPage/TelegramLoginButton';
import { AppConfig } from '../../../Config';
import GoogleIdentityButton from '../LoginPage/GoogleIdentityButton';

const emptyDelivery: DeliveryInformationData = {
  name: '',
  lastName: '',
  phone: '',
  provider: DeliveryProvider.NovaPost,
  destination: DeliveryDestination.Department,
  city: '',
  number: '',
  displayName: '',
  countryCode: 'UA',
  isPrimary: true,
  novaPoshtaAddress: null,
  ukrPoshtaAddress: null,
};

const providerLabel: Record<DeliveryProvider, string> = {
  [DeliveryProvider.NovaPost]: 'Нова пошта',
  [DeliveryProvider.UkrPost]: 'Укрпошта',
  [DeliveryProvider.Self]: 'Самовивіз',
};

const renderProviderValue = (selected: unknown) => {
  const provider = String(selected) as DeliveryProvider;

  return (
    <span className="provider-select-value">
      <DeliveryProviderIcon provider={provider} />
      <span>{providerLabel[provider] ?? provider}</span>
    </span>
  );
};

const destinationLabel: Record<DeliveryDestination, string> = {
  [DeliveryDestination.Department]: 'Відділення',
  [DeliveryDestination.ParcelLocker]: 'Поштомат',
  [DeliveryDestination.Address]: "Кур'єр",
};

const authConnectionConfig: Array<{
  type: AuthConnectionType;
  label: string;
  Icon: React.ElementType;
  connectTooltip: string;
  linkedTooltip: string;
}> = [
  {
    type: 'Email',
    label: 'Пошта',
    Icon: EmailIcon,
    connectTooltip: "Прив'язати пошту",
    linkedTooltip: "Пошта прив'язана",
  },
  {
    type: 'Phone',
    label: 'Телефон',
    Icon: PhoneIphoneIcon,
    connectTooltip: "Прив'язати телефон",
    linkedTooltip: "Телефон прив'язаний",
  },
  {
    type: 'Google',
    label: 'Google',
    Icon: GoogleIcon,
    connectTooltip: "Прив'язати Google",
    linkedTooltip: "Google прив'язаний",
  },
  {
    type: 'Telegram',
    label: 'Telegram',
    Icon: TelegramIcon,
    connectTooltip: "Прив'язати Telegram",
    linkedTooltip: "Telegram прив'язаний",
  },
];

type AuthConnectionPayload = {
  type?: AuthConnectionType | string | number | null;
  Type?: AuthConnectionType | string | number | null;
  provider?: AuthConnectionType | string | number | null;
  Provider?: AuthConnectionType | string | number | null;
  isLinked?: boolean | string | number | null;
  IsLinked?: boolean | string | number | null;
  linked?: boolean | string | number | null;
  Linked?: boolean | string | number | null;
};

const authConnectionTypeAliases: Record<AuthConnectionType, string[]> = {
  Email: ['email', 'mail'],
  Phone: ['phone', 'mobile', 'mobilephone', 'telephone'],
  Google: ['google', 'googleoauth', 'googleoidc'],
  Telegram: ['telegram', 'tg'],
};

const authConnectionNumericTypes: Record<number, AuthConnectionType> = {
  0: 'Email',
  1: 'Google',
  2: 'Telegram',
  3: 'Phone',
};

const normalizeAuthConnectionType = (connection: AuthConnectionPayload): AuthConnectionType | null => {
  const rawType = connection.type ?? connection.Type ?? connection.provider ?? connection.Provider;

  if (typeof rawType === 'number') {
    return authConnectionNumericTypes[rawType] ?? null;
  }

  if (typeof rawType !== 'string') {
    return null;
  }

  const normalized = rawType.trim().toLowerCase().replace(/[\s_-]/g, '');

  return authConnectionConfig.find((config) =>
    authConnectionTypeAliases[config.type].includes(normalized)
  )?.type ?? null;
};

const isConnectionLinkedValue = (value: unknown) =>
  value === true ||
  value === 1 ||
  (typeof value === 'string' && ['true', '1', 'yes', 'linked', 'connected'].includes(value.trim().toLowerCase()));

const isConnectionLinked = (connection: AuthConnectionPayload) =>
  isConnectionLinkedValue(connection.isLinked ?? connection.IsLinked ?? connection.linked ?? connection.Linked);

const formatPrice = (value: number) => `${Number(value || 0).toFixed(2)} грн`;

const orderStatusLabels: Record<string, string> = {
  '0': 'Нове',
  Created: 'Нове',
  '1': 'Оплачено',
  Paid: 'Оплачено',
  '2': 'Комплектація',
  Complectation: 'Комплектація',
  '3': 'Відправлено',
  Shipped: 'Відправлено',
  '4': 'Повернення',
  Returning: 'Повернення',
  '5': 'Завершено',
  Completed: 'Завершено',
  '6': 'Скасовано',
  Cancelled: 'Скасовано',
  '7': 'Потрібне уточнення',
  NeedsClarification: 'Потрібне уточнення',
  '8': 'Запаковано',
  Packed: 'Запаковано',
};

const getOrderStatusLabel = (status: number | string) =>
  orderStatusLabels[String(status)] ?? String(status);

const toDeliveryPayload = (delivery: DeliveryInformationData): DeliveryInformationPayload => ({
  label: delivery.label?.trim() || null,
  name: delivery.name.trim(),
  lastName: delivery.lastName.trim(),
  phone: delivery.phone.trim(),
  provider: delivery.provider,
  destination: delivery.provider === DeliveryProvider.Self ? DeliveryDestination.Address : delivery.destination,
  city: delivery.provider === DeliveryProvider.Self ? '' : delivery.city.trim(),
  number: delivery.provider === DeliveryProvider.Self ? '' : delivery.number.trim(),
  displayName: getDeliveryDisplayName(delivery),
  countryCode: delivery.countryCode || 'UA',
  regionName: delivery.regionName || null,
  districtName: delivery.districtName || null,
  isPrimary: delivery.isPrimary,
  novaPoshtaAddress: delivery.provider === DeliveryProvider.NovaPost ? delivery.novaPoshtaAddress ?? null : null,
  ukrPoshtaAddress: delivery.provider === DeliveryProvider.UkrPost ? delivery.ukrPoshtaAddress ?? null : null,
});

const createEmptyDelivery = (profile?: UserInfoData | null): DeliveryInformationData => ({
  ...emptyDelivery,
  name: profile?.name || '',
  lastName: profile?.lastName || '',
  phone: profile?.phoneNumber || '',
  isPrimary: (profile?.deliveryInformation?.length ?? 0) === 0,
});

const getDeliveryDisplayName = (delivery: DeliveryInformationData) =>
  delivery.provider === DeliveryProvider.Self
    ? 'Самовивіз'
    : delivery.displayName?.trim() || [delivery.city, delivery.number].filter(Boolean).join(', ');

const clearProviderDetails = (delivery: DeliveryInformationData): DeliveryInformationData => ({
  ...delivery,
  city: '',
  number: '',
  displayName: '',
  regionName: null,
  districtName: null,
  novaPoshtaAddress: null,
  ukrPoshtaAddress: null,
});

const clearDeliveryTarget = (delivery: DeliveryInformationData, destination: DeliveryDestination): DeliveryInformationData => {
  if (delivery.provider !== DeliveryProvider.NovaPost) {
    return { ...delivery, destination };
  }

  const nova = delivery.novaPoshtaAddress;
  return {
    ...delivery,
    destination,
    number: '',
    displayName: '',
    novaPoshtaAddress: nova
      ? {
          cityRef: nova.cityRef,
          settlementRef: nova.settlementRef,
          cityName: nova.cityName,
          areaName: nova.areaName,
          regionName: nova.regionName,
          settlementType: nova.settlementType,
        }
      : null,
  };
};

function ProfilePage() {
  const { logout, user } = useAuth();
  const { clearLocalCart } = useCart();
  const [profile, setProfile] = useState<UserInfoData | null>(null);
  const [orders, setOrders] = useState<OrderData[]>([]);
  const [form, setForm] = useState<DeliveryInformationData>(emptyDelivery);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [ordersLoading, setOrdersLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [profileError, setProfileError] = useState<string | null>(null);
  const [ordersError, setOrdersError] = useState<string | null>(null);
  const [authConnectionMessage, setAuthConnectionMessage] = useState<string | null>(null);
  const [authConnectionError, setAuthConnectionError] = useState<string | null>(null);
  const [linkingConnection, setLinkingConnection] = useState<AuthConnectionType | null>(null);
  const [googleLinkDialogOpen, setGoogleLinkDialogOpen] = useState(false);

  const isTelegramLinkAvailable = Boolean(Number(AppConfig.TelegramLoginClientId));
  const isGoogleLinkAvailable = Boolean(AppConfig.GoogleLoginClientId);

  const loadProfile = async () => {
    setLoading(true);
    setProfileError(null);
    try {
      const response = await UsersService.getCurrentUser();
      setProfile(response);
      setForm((prev) => ({
        ...prev,
        name: response.name || prev.name,
        lastName: response.lastName || prev.lastName,
        phone: response.phoneNumber || prev.phone,
        isPrimary: (response.deliveryInformation?.length ?? 0) === 0,
      }));
      return response;
    } catch {
      setProfileError('Не вдалося завантажити профіль. Оновіть сторінку або увійдіть ще раз.');
      return null;
    } finally {
      setLoading(false);
    }
  };

  const loadOrders = async () => {
    setOrdersLoading(true);
    setOrdersError(null);
    try {
      const response = await orderService.getHistory();
      setOrders(response.items ?? []);
    } catch {
      setOrders([]);
      setOrdersError('Історію оренд поки не вдалося завантажити.');
    } finally {
      setOrdersLoading(false);
    }
  };

  useEffect(() => {
    loadProfile();
    loadOrders();
  }, []);

  const resetForm = (profileOverride?: UserInfoData | null) => {
    setEditingId(null);
    setForm(createEmptyDelivery(profileOverride ?? profile));
  };

  const handleLogout = () => {
    logout();
    clearLocalCart();
  };

  const isAuthConnectionLinked = (type: AuthConnectionType) =>
    profile?.authConnections?.some((connection) => {
      const payload = connection as AuthConnectionPayload;
      return normalizeAuthConnectionType(payload) === type && isConnectionLinked(payload);
    }) ?? false;

  const handleLinkTelegram = async () => {
    if (!isTelegramLinkAvailable || isAuthConnectionLinked('Telegram')) {
      return;
    }

    try {
      setAuthConnectionMessage(null);
      setAuthConnectionError(null);
      setLinkingConnection('Telegram');
      const telegramPayload = await requestTelegramLoginData();
      const updatedProfile = await UsersService.linkTelegram(telegramPayload);
      setProfile(updatedProfile);
      setAuthConnectionMessage("Telegram прив'язано до акаунта.");
    } catch (error) {
      if (error instanceof ApiError && error.status === 409) {
        setAuthConnectionError("Цей Telegram вже прив'язаний до іншого акаунта.");
      } else {
        setAuthConnectionError("Не вдалося прив'язати Telegram. Спробуйте ще раз.");
      }
    } finally {
      setLinkingConnection(null);
    }
  };

  const handleLinkGoogleCredential = async (idToken: string) => {
    if (!isGoogleLinkAvailable || isAuthConnectionLinked('Google')) {
      return;
    }

    try {
      setAuthConnectionMessage(null);
      setAuthConnectionError(null);
      setLinkingConnection('Google');
      const updatedProfile = await UsersService.linkGoogle({ idToken });
      setProfile(updatedProfile);
      setGoogleLinkDialogOpen(false);
      setAuthConnectionMessage("Google прив'язано до акаунта.");
    } catch (error) {
      if (error instanceof ApiError && error.status === 409) {
        setAuthConnectionError("Цей Google акаунт або email вже прив'язаний до іншого акаунта.");
      } else {
        setAuthConnectionError("Не вдалося прив'язати Google. Спробуйте ще раз.");
      }
    } finally {
      setLinkingConnection(null);
    }
  };

  const renderAuthConnectionIndicator = (config: (typeof authConnectionConfig)[number]) => {
    const isLinked = isAuthConnectionLinked(config.type);
    const isTelegram = config.type === 'Telegram';
    const isGoogle = config.type === 'Google';
    const isPending = linkingConnection === config.type;
    const isAvailable =
      !isLinked &&
      ((isTelegram && isTelegramLinkAvailable) ||
        (isGoogle && isGoogleLinkAvailable));
    const isDisabled = isLinked || !isAvailable || isPending;
    const Icon = config.Icon;
    const handleClick = isTelegram
      ? handleLinkTelegram
      : isGoogle
        ? () => setGoogleLinkDialogOpen(true)
        : undefined;

    return (
      <Tooltip key={config.type} title={isLinked ? config.linkedTooltip : config.connectTooltip} arrow>
        <span className="auth-connection-tooltip-anchor">
          <IconButton
            className={[
              'auth-connection-button',
              isLinked ? 'auth-connection-button--linked' : 'auth-connection-button--inactive',
              isAvailable ? 'auth-connection-button--available' : '',
            ].filter(Boolean).join(' ')}
            aria-label={isLinked ? config.linkedTooltip : config.connectTooltip}
            disabled={isDisabled}
            onClick={handleClick}
          >
            <Icon fontSize="small" />
            <span className="auth-connection-status" aria-hidden="true">
              {isLinked ? <CheckCircleIcon /> : null}
            </span>
          </IconButton>
        </span>
      </Tooltip>
    );
  };

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setSaving(true);
    setMessage(null);
    try {
      const payload = toDeliveryPayload(form);
      if (editingId !== null) {
        await UsersService.updateDeliveryInformation(editingId, payload);
        setMessage('Адресу доставки оновлено.');
      } else {
        await UsersService.addDeliveryInformation(payload);
        setMessage('Адресу доставки додано.');
      }
      const refreshedProfile = await loadProfile();
      resetForm(refreshedProfile);
    } catch {
      setMessage('Не вдалося зберегти адресу доставки. Перевірте поля.');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (delivery: DeliveryInformationData) => {
    setEditingId(delivery.id ?? null);
    setForm({
      ...emptyDelivery,
      ...delivery,
      displayName: getDeliveryDisplayName(delivery),
      countryCode: delivery.countryCode || 'UA',
      novaPoshtaAddress: delivery.novaPoshtaAddress ?? null,
      ukrPoshtaAddress: delivery.ukrPoshtaAddress ?? null,
    });
  };

  const handleDelete = async (id?: number) => {
    if (id == null) return;
    try {
      await UsersService.deleteDeliveryInformation(id);
      await loadProfile();
    } catch {
      setMessage('Не вдалося видалити адресу доставки.');
    }
  };

  if (loading) {
    return (
      <div className="profile default-container">
        <CircularProgress />
      </div>
    );
  }

  return (
    <div className="profile default-container">
      <section className="profile-hero">
        <div className="profile-hero-main">
          <div>
            <Typography variant="h4">Привіт, {profile?.name || user?.name}</Typography>
            <Typography color="text.secondary">{profile?.email || user?.email}</Typography>
          </div>
          <div className="auth-connections" aria-label="Способи входу">
            {authConnectionConfig.map(renderAuthConnectionIndicator)}
          </div>
        </div>
        <Button id="logout-button" variant="outlined" onClick={handleLogout}>
          Вийти
        </Button>
      </section>

      {profileError && <Alert severity="error" className="profile-alert">{profileError}</Alert>}
      {authConnectionError && <Alert severity="error" className="profile-alert">{authConnectionError}</Alert>}
      {authConnectionMessage && <Alert severity="success" className="profile-alert">{authConnectionMessage}</Alert>}

      <section className="profile-layout">
        <div className="profile-panel">
          <Typography variant="h6">Адреси доставки</Typography>
          <div className="delivery-list">
            {profile?.deliveryInformation?.length ? (
              profile.deliveryInformation.map((delivery) => (
                <article key={delivery.id} className="delivery-card">
                  <div>
                    <div className="delivery-card-title">
                      <DeliveryProviderIcon provider={delivery.provider} />
                      <strong>
                        {delivery.name} {delivery.lastName}
                      </strong>
                    </div>
                    <p>
                      {providerLabel[delivery.provider]} · {destinationLabel[delivery.destination]}
                    </p>
                    <p>
                      {getDeliveryDisplayName(delivery)}
                    </p>
                    <p>{delivery.phone}</p>
                    {delivery.isPrimary && <span className="primary-badge">Основна</span>}
                  </div>
                  <div className="delivery-actions">
                    <Button size="small" onClick={() => handleEdit(delivery)}>
                      Редагувати
                    </Button>
                    <Button size="small" color="error" onClick={() => handleDelete(delivery.id)}>
                      Видалити
                    </Button>
                  </div>
                </article>
              ))
            ) : (
              <p className="muted">Адрес ще немає. Додайте першу, щоб оформлювати оренду швидше.</p>
            )}
          </div>
        </div>

        <form className="profile-panel delivery-form" onSubmit={handleSubmit}>
          <Typography variant="h6">{editingId !== null ? 'Редагувати адресу' : 'Нова адреса доставки'}</Typography>

          <div className="form-grid">
            <TextField label="Ім'я" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
            <TextField
              label="Прізвище"
              value={form.lastName}
              onChange={(e) => setForm({ ...form, lastName: e.target.value })}
              required
            />
            <TextField label="Телефон" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} required />
            <TextField
              select
              label="Служба доставки"
              value={form.provider}
              SelectProps={{ renderValue: renderProviderValue }}
              onChange={(e) => {
                const provider = e.target.value as DeliveryProvider;
                setForm(clearProviderDetails({
                  ...form,
                  provider,
                  destination: provider === DeliveryProvider.Self ? DeliveryDestination.Address : DeliveryDestination.Department,
                }));
              }}
            >
              {Object.entries(providerLabel).map(([value, label]) => (
                <MenuItem key={value} value={value} className="provider-menu-item">
                  <DeliveryProviderIcon provider={value} />
                  {label}
                </MenuItem>
              ))}
            </TextField>
            {form.provider === DeliveryProvider.NovaPost ? (
              <NovaPoshtaAddressFields
                value={form}
                onChange={setForm}
                destinationControl={(
                  <TextField
                    select
                    label="Тип доставки"
                    value={form.destination}
                    onChange={(e) => setForm(clearDeliveryTarget(form, e.target.value as DeliveryDestination))}
                  >
                    {Object.entries(destinationLabel).map(([value, label]) => (
                      <MenuItem key={value} value={value}>
                        {label}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              />
            ) : form.provider === DeliveryProvider.Self ? (
              <SelfPickupFields value={form} onChange={setForm} />
            ) : (
              <>
                <TextField
                  select
                  label="Тип доставки"
                  value={form.destination}
                  onChange={(e) => setForm(clearDeliveryTarget(form, e.target.value as DeliveryDestination))}
                >
                  {Object.entries(destinationLabel).map(([value, label]) => (
                    <MenuItem key={value} value={value}>
                      {label}
                    </MenuItem>
                  ))}
                </TextField>
                <TextField
                  label="Місто"
                  value={form.city}
                  onChange={(e) => {
                    const city = e.target.value;
                    setForm({
                      ...form,
                      city,
                      displayName: [city, form.number].filter(Boolean).join(', '),
                    });
                  }}
                  required
                />
                <TextField
                  label="Номер відділення, індекс або адреса"
                  value={form.number}
                  onChange={(e) => {
                    const number = e.target.value;
                    setForm({
                      ...form,
                      number,
                      displayName: [form.city, number].filter(Boolean).join(', '),
                    });
                  }}
                  required
                />
              </>
            )}
          </div>

          <FormControlLabel
            control={
              <Checkbox
                checked={form.isPrimary}
                onChange={(e) => setForm({ ...form, isPrimary: e.target.checked })}
              />
            }
            label="Зробити основною адресою"
          />

          {message && <Alert severity={message.startsWith('Не') ? 'error' : 'success'}>{message}</Alert>}

          <div className="form-actions">
            {editingId !== null && (
              <Button type="button" variant="text" onClick={() => resetForm()}>
                Скасувати
              </Button>
            )}
            <Button type="submit" variant="contained" disabled={saving}>
              {saving ? 'Зберігаємо...' : 'Зберегти адресу'}
            </Button>
          </div>
        </form>
      </section>

      <section className="profile-panel orders-history">
        <div className="orders-history-header">
          <Typography variant="h6">Історія оренд</Typography>
          <Button size="small" variant="outlined" onClick={loadOrders} disabled={ordersLoading}>
            Оновити
          </Button>
        </div>

        {ordersLoading ? (
          <CircularProgress size={24} />
        ) : ordersError ? (
          <Alert severity="warning">{ordersError}</Alert>
        ) : orders.length ? (
          <div className="orders-list">
            {orders.map((order) => (
              <article key={order.id} className="order-card">
                <div className="order-card-main">
                  <strong>Оренда #{order.id}</strong>
                  <span>{new Date(order.orderDate).toLocaleDateString('uk-UA')}</span>
                  <span>{getOrderStatusLabel(order.orderStatus)}</span>
                </div>
                <div className="order-card-items">
                  {order.items.map((item) => (
                    <span key={`${order.id}-${item.itemId}`}>
                      {item.name} x {item.count}
                    </span>
                  ))}
                </div>
                <strong>{formatPrice(order.totalPrice)}</strong>
              </article>
            ))}
          </div>
        ) : (
          <p className="muted">Оренд ще немає. Коли оформите першу, вона з'явиться тут.</p>
        )}
      </section>

      <Dialog
        open={googleLinkDialogOpen}
        onClose={() => {
          if (linkingConnection !== 'Google') {
            setGoogleLinkDialogOpen(false);
          }
        }}
        PaperProps={{ className: 'google-link-dialog' }}
      >
        <DialogTitle>Прив'язати Google</DialogTitle>
        <DialogContent>
          <div className="google-link-dialog-content">
            <p>Оберіть Google акаунт, який хочете використовувати для входу в Readerly.</p>
            <GoogleIdentityButton
              disabled={linkingConnection === 'Google'}
              text="continue_with"
              onCredential={handleLinkGoogleCredential}
              onError={(message) =>
                setAuthConnectionError(message || "Не вдалося прив'язати Google. Спробуйте ще раз.")
              }
            />
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default ProfilePage;
